import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, inject, Input } from "@angular/core";
import {
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
} from "@angular/forms";

import { I18n } from "@intocare/interfaces";
import { clean } from "@intocare/util";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => MultiTextAreaInputComponent),
	multi: true,
};

@Component({
	selector: "ic-multi-text-area",
	styleUrls: ["multi-text-area.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./multi-text-area.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class MultiTextAreaInputComponent implements ControlValueAccessor {
	private cdRef = inject(ChangeDetectorRef);
	private formBuilder = inject(UntypedFormBuilder);

	@Input() label: string;
	@Input() placeholder = "";
	@Input() required = true;
	@Input() help: string;
	@Input() error = false;
	@Input() missing = false;
	@Input() missingText: string;

	@Input()
	set languagesWithErrors(value: string[]) {
		this.errors = new Set(value);
	}

	@Input()
	set languages(value: string[]) {
		if (value.length === 0) {
			return;
		}

		for (const lang of value) {
			this.innerForm.addControl(lang, new UntypedFormControl());
		}

		this.langs = value;
		this.setCurrentLang(value[0]);
		this.touched = new Set();
	}

	value: any = {};
	touched = new Set<string>();
	currentLang: string;
	langs: string[] = [];
	errors = new Set<string>();
	innerForm: UntypedFormGroup = this.formBuilder.group({});
	disabled = false;

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	writeValue(value: any) {
		this.touched = new Set([]);
		this.innerForm.reset({ ...value });
		if (this.langs && this.langs.length > 0) {
			this.currentLang = this.langs[0];
		}
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		if (isDisabled) {
			this.innerForm.disable();
		} else {
			this.innerForm.enable();
		}
		this.cdRef.markForCheck();
	}

	hasValue(language: string) {
		return Boolean(this.value[language]);
	}

	onChange() {
		this.onModelChange(this.cleanValue(this.innerForm.value));
	}

	onTouched(language: string) {
		this.touched.add(language);

		if (this.touched.size === this.langs.length) {
			this.onTouch();
		}
	}

	setCurrentLang(value: string) {
		this.onTouched(value);
		this.currentLang = value;
		this.cdRef.detectChanges();
	}

	private cleanValue(value: I18n | string) {
		if (typeof value === "string") {
			return value;
		}

		const cleaned = clean(value);

		return Object.keys(cleaned).length > 0 ? cleaned : "";
	}
}
